import React from "react";
import Modal2, { Modal2Footer, Modal2Button } from "../common/Modal2";
import {
  getWorkflowStatusFallbackLabel,
  LeaveTypeLabel,
  LeaveTypesEnum,
  WorkflowStatusCode,
} from "../../utils/constants";
import { formatDate } from "../../utils/timeUtils";
import { getWorkflowStatusVariant } from "../../utils/badgeVariants";
import Badge from "../common/Badge";
import { capitalizeWords } from "../../utils/nameUtils";

export interface LeaveViewRecord {
  id: string;
  userId: string;
  employeeId?: string;
  userName: string;
  leaveType: string;
  leaveTypeName?: string;
  startDate: string;
  endDate: string;
  days: number;
  reason: string;
  status: WorkflowStatusCode;
  statusLabel?: string;
  currentApproverName?: string;
  appliedDate: string;
  approvedBy?: string;
  approvedDate?: string;
  rejectionReason?: string;
  department?: string;
  departmentName?: string;
  reportingManagerName?: string;
  functionalManagerName?: string;
  approvedByName?: string;
}

interface LeaveViewModalProps {
  record: LeaveViewRecord | null;
  onClose: () => void;
}

const LeaveViewModal: React.FC<LeaveViewModalProps> = ({ record, onClose }) => {

  return (
    <Modal2
      isOpen={!!record}
      onClose={onClose}
      title="Leave Details"
      size="lg"
      footer={
        <Modal2Footer justify="end">
          <Modal2Button variant="secondary" onClick={onClose}>
            Close
          </Modal2Button>
        </Modal2Footer>
      }
    >

      {record && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Employee ID
              </p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                {record.employeeId || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                User
              </p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                {capitalizeWords(record.userName)}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Department
              </p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                {record.departmentName || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Reporting Manager
              </p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                {capitalizeWords(record.reportingManagerName) || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Functional Manager
              </p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                {capitalizeWords(record.functionalManagerName) || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Type
              </p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
             {LeaveTypeLabel[record.leaveType as LeaveTypesEnum] || record.leaveTypeName || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Days
              </p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                {record.days}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Duration
              </p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                {formatDate(record.startDate)} → {formatDate(record.endDate)}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Status
              </p>
              <Badge
                variant={getWorkflowStatusVariant(record.status)}
                size="small"
              >
                {record.statusLabel ||
                  getWorkflowStatusFallbackLabel(
                    record.status,
                    record.currentApproverName
                  )}
              </Badge>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Applied Date
              </p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
             {record.appliedDate
  ? formatDate(record.appliedDate.split("T")[0])
  : "-"}
              </p>
            </div>
            {record.approvedBy && (
              <div className="md:col-span-2">
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Approved By / Date
                </p>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {capitalizeWords(record.approvedByName)}{" "}
                  on {formatDate(record.approvedDate?.split("T")[0] || "")}
                </p>
              </div>
            )}
            {record.rejectionReason && (
              <div className="md:col-span-2">
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Rejection Reason
                </p>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {record.rejectionReason}
                </p>
              </div>
            )}
            <div className="md:col-span-2">
              <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                Reason
              </p>
              <p className="text-sm font-semibold text-[var(--color-text-primary)] whitespace-pre-wrap">
                {record.reason}
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal2>
  );
};

export default LeaveViewModal;
